import {Fragment} from 'react'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Typography from '@mui/material/Typography'

/**
 * The screen an authorization lands on when it cannot be granted.
 *
 * Every refusal is the same card — an alert, a code support can quote back, and the exits that mean
 * something for that particular failure — so a new refusal from the API is a row in `variants`
 * rather than a screen of its own. Which row to show is the controller's call, since only it sees
 * the response.
 *
 * Two kinds of exit repeat here and are not interchangeable. The recovery action is the one that
 * changes the user's situation: leaving an administrator account, signing back in, retrying a call
 * that failed for no lasting reason. The other exit ends the request, and which form it takes is
 * not a property of the variant but of what came back: a refusal the server answered carries a
 * redirect built behind signature verification, so the client can be told why, while a failure that
 * produced no such redirect can only be cancelled. `canReturnToClient` is the controller reporting
 * which of those it is holding.
 */
const recoveries = {
  signOut: {label: 'Sign out', color: 'error'},
  signIn: {label: 'Sign in', color: 'primary'},
  retry: {label: 'Try again', color: 'primary'}
}

const variants = {
  adminAccount: {
    code: 'access_denied',
    severity: 'error',
    title: () => "Administrator accounts can't connect apps",
    message: client => `You're signed in as an administrator. ${client} connects to personal accounts only. Sign out, then sign back in with your own account to continue.`,
    recovery: 'signOut'
  },
  invalidRequest: {
    code: 'invalid_request',
    severity: 'warning',
    title: () => 'This sign-in request is incomplete',
    message: client => `${client} sent a request that's missing information Accounts needs. Head back and start again — there's nothing to fix on this side.`
  },
  sessionExpired: {
    code: 'login_required',
    severity: 'info',
    title: () => 'Your session expired',
    message: client => `Sign in again and you'll come straight back here to finish connecting ${client}.`,
    recovery: 'signIn'
  },
  serverError: {
    code: 'server_error',
    severity: 'error',
    title: client => `We couldn't finish connecting ${client}`,
    message: () => 'Something went wrong on our side and nothing was changed on your account. Try again — if it keeps failing, head back and start over.',
    recovery: 'retry'
  },
  networkError: {
    code: 'network_error',
    severity: 'error',
    title: () => "We couldn't reach Accounts",
    message: client => `The request never made it out, so nothing was changed on your account and ${client} hasn't been told anything. Check your connection and try again.`,
    recovery: 'retry'
  }
}

const SingleSignOnError = props => {
  const variant = variants[props.variant] || variants.serverError
  const recovery = recoveries[variant.recovery]
  const recover = {signOut: props.onSignOut, signIn: props.onSignIn, retry: props.onRetry}[variant.recovery]
  const detail = [variant.code, props.statusCode, __DEV__ && props.message].filter(Boolean).join(' · ')

  return (
    <Fragment>
      <Alert severity={variant.severity}>
        <AlertTitle>{variant.title(props.client.name)}</AlertTitle>
        {variant.message(props.client.name)}
      </Alert>
      <Typography variant='body2' sx={{marginTop: '12px', opacity: 0.65, textAlign: 'right'}}>{detail}</Typography>
      <ButtonGroup className='button-group' component='div' sx={{justifyContent: 'flex-end', width: '100%'}}>
        {props.canReturnToClient ?
          <Button onClick={props.onReturnToClient}>Return to {props.client.name}</Button> :
          <Button onClick={props.onCancel}>Cancel</Button>}
        {recovery && <Button onClick={recover} color={recovery.color}>{recovery.label}</Button>}
      </ButtonGroup>
    </Fragment>
  )
}

export default SingleSignOnError
